import type { ReactNode } from "react";

export interface ListItemProps {
  leading?: ReactNode;
  children: ReactNode;

  description?: ReactNode;
  trailing?: ReactNode;

  divider?: boolean;
  onClick?: () => void;

  /** True when rendered flat on the page instead of inside a Card. The page's own background is already surface-base, so the hover fill needs to be the darker interactive-hover token instead to stay visible. */
  flat?: boolean;
}

export function ListItem({
  leading,
  children,
  description,
  trailing,
  divider = false,
  onClick,
  flat = false,
}: Readonly<ListItemProps>) {
  const classes = [
    "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left transition-colors",
    onClick ? ["cursor-pointer", flat ? "hover:bg-interactive-hover" : "hover:bg-surface-base"].join(" ") : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {leading && <span className="shrink-0">{leading}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate [font:var(--type-body-xs)] [font-weight:var(--weight-medium)] text-content-primary">
          {children}
        </span>
        {description && <span className="mt-1 block truncate text-xs text-content-tertiary">{description}</span>}
      </span>
      {trailing && <span className="shrink-0">{trailing}</span>}
    </>
  );

  const row = onClick ? (
    <button type="button" onClick={onClick} className={[classes, "btn-focus-ring"].join(" ")}>
      {content}
    </button>
  ) : (
    <div className={classes}>{content}</div>
  );

  if (!divider) return row;

  return (
    <>
      <div className={[flat ? "" : "mx-4", "border-t border-border-subtle"].filter(Boolean).join(" ")} />
      {row}
    </>
  );
}
